/* eslint-disable max-lines */
import {
  BottomSheet,
  BottomSheetIllustrationContent,
  type UseModalState,
  useModalState,
} from "@clipboard-health/ui-components";
import { BottomSheetLoadingContent } from "@clipboard-health/ui-components";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useToast } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useScreenBrightness } from "@src/appV2/lib/utils";
import { useTimeoutTimer } from "@src/appV2/lib/utils/useTimeoutTimer";
import { ClockOutChartingReminder } from "@src/appV2/redesign/Charting/ClockOutChartingReminder";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { getSafeAreaPaddings } from "@src/appV2/redesign/utils/getSafeAreaPaddings";
import { useSkippableWait } from "@src/appV2/redesign/utils/useSkippableWait";
import {
  getWorkerShiftPath,
  type GetWorkerShiftResponse,
} from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import {
  ShiftMarkedNonIpReasons,
  useMarkShiftNonInstant,
} from "@src/appV2/redesign/WorkerShifts/api/useMarkShiftNonInstant";
import {
  ActionCheckType,
  ShiftStage,
  useRecordShiftTimekeepingAction,
} from "@src/appV2/redesign/WorkerShifts/api/useRecordShiftTimekeepingAction";
import {
  SHIFT_DETAILS_FULL_PATH,
  SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH,
} from "@src/appV2/redesign/WorkerShifts/path";
import { NfcHashValidationAction } from "@src/appV2/redesign/WorkerShifts/Timekeeping/NfcValidation/api/types";
import { WorkerNfcValidation } from "@src/appV2/redesign/WorkerShifts/Timekeeping/NfcValidation/WorkerNfcValidation";
import { AcceptNonInstantPayContent } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/AcceptNonInstantPayContent";
import { DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/constants";
import { TimekeepingActionSuccessContent } from "@src/appV2/redesign/WorkerShifts/Timekeeping/TimekeepingActions/TimekeepingActionSuccessContent";
import { useGetShiftMutation } from "@src/appV2/Shifts/Shift/api/useGetShiftMutation";
import { type TouchpointCodeGeneratorProps } from "@src/appV2/Shifts/Shift/TouchpointIntegration/touchpointCodeGenerator";
import { useTouchpointFirstViewTime } from "@src/appV2/Shifts/Shift/TouchpointIntegration/useTouchpointFirstViewTime";
import { type TimeRange, type WorkerShift } from "@src/appV2/Shifts/Shift/types";
import { useGetShiftFeedbackClockOutConfig } from "@src/appV2/Shifts/Shift/useGetShiftFeedbackClockOutConfig";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { QrCodeDisplay } from "../Stages/QrCodeDisplay";
import { ScanInstructions } from "../Stages/ScanInstructions";
import { TouchpointSkipWarning } from "../Stages/TouchpointSkipWarning";

export interface ClockOutTouchpointBottomSheetProps {
  modalState: UseModalState;
  workerId: string;
  workplaceId: string;
  workplaceName: string;
  kioskLocation: string;
  qrCodeProps: TouchpointCodeGeneratorProps;
  shift: WorkerShift;
  onTouchpointFailure: () => void;
  timeToAllowSkipInSec: number;
  isMissedPunchRequestEnabled: boolean;
}

type ClockOutTouchpointStep =
  | "charting-reminder"
  | "clock-out-confirm"
  | "instructions"
  | "qr-display"
  | "clock-out-success"
  | "skip-warning"
  | "accept-non-instant-pay"
  | "requesting-non-instant-pay"
  | "touchpoint-nfc-check";

const REFETCH_SHIFT_INTERVAL_IN_MS = 3000;

export function ClockOutTouchpointBottomSheet(props: ClockOutTouchpointBottomSheetProps) {
  const {
    modalState,
    workerId,
    workplaceId,
    workplaceName,
    kioskLocation,
    qrCodeProps,
    shift,
    onTouchpointFailure,
    timeToAllowSkipInSec,
    isMissedPunchRequestEnabled,
  } = props;

  const shiftId = shift._id;

  const history = useHistory();
  const { showErrorToast } = useToast();
  const queryClient = useQueryClient();

  const chartingReminderModalState = useModalState();
  const { chartingRecallEnabled: shouldShowChartingReminder } =
    useGetShiftFeedbackClockOutConfig(workplaceId);

  const firstStep: ClockOutTouchpointStep = shouldShowChartingReminder
    ? "charting-reminder"
    : "clock-out-confirm";

  const [step, setStep] = useState<ClockOutTouchpointStep>(firstStep);
  const { wait, skip: skipWait } = useSkippableWait();

  const shiftDetailsPollingInterval = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    if (modalState.modalIsOpen && step === "charting-reminder") {
      chartingReminderModalState.openModal();
    }
  }, [modalState.modalIsOpen, step, chartingReminderModalState]);

  const firstViewTime = useTouchpointFirstViewTime({
    enabled: modalState.modalIsOpen && step === "qr-display",
    shiftId,
    clockAction: "clock-out",
  });

  const { secondsLeft: secondsLeftForNotWorking } = useTimeoutTimer({
    initialEndTime: firstViewTime ? firstViewTime + timeToAllowSkipInSec * 1000 : Date.now(),
  });

  useLogEffect(
    APP_V2_USER_EVENTS.CLOCK_OUT_MODAL_OPENED,
    {
      shiftId,
      workerId,
      workplaceId,
      clockOutType: "touchpoint",
    },
    { enabled: modalState.modalIsOpen }
  );

  const maximizeBrightnessOnQrDisplay = useCbhFlag(
    CbhFeatureFlag.MAXIMIZE_BRIGHTNESS_ON_TOUCHPOINT_QR_DISPLAY_STEP,
    {
      defaultValue: false,
    }
  );

  // Use screen brightness hook to maximize brightness during QR display
  useScreenBrightness(
    1, // Set brightness to maximum (100%)
    maximizeBrightnessOnQrDisplay && modalState.modalIsOpen && step === "qr-display",
    (error, operation) => {
      if (operation === "set") {
        logError(APP_V2_APP_EVENTS.TOUCHPOINT_QR_SET_MAX_BRIGHTNESS_FAILURE, {
          error,
        });
      } else if (operation === "restore") {
        logError(APP_V2_APP_EVENTS.TOUCHPOINT_QR_RESTORE_BRIGHTNESS_FAILURE, {
          error,
        });
      }
    }
  );

  const { mutateAsync: markNonInstant } = useMarkShiftNonInstant();
  const { mutateAsync: recordTimekeepingAction } = useRecordShiftTimekeepingAction();

  const shiftDetailsPath = generatePath(SHIFT_DETAILS_FULL_PATH, {
    shiftId,
  });
  const submitTimesheetPath = `${shiftDetailsPath}${SHIFT_DETAILS_SUBMIT_TIMESHEET_PATH}`;

  const closeBottomSheet = useCallback(
    (shiftClockInOut?: TimeRange) => {
      logEvent(APP_V2_USER_EVENTS.CLOCK_OUT_MODAL_CLOSED, {
        shiftId,
        workerId,
        workplaceId,
        clockOutType: "touchpoint",
        step,
      });
      skipWait();
      modalState.closeModal();
      chartingReminderModalState.closeModal();
      setStep(firstStep);

      if (isDefined(shiftClockInOut)) {
        queryClient.setQueryData<GetWorkerShiftResponse>([getWorkerShiftPath(shiftId)], (result) =>
          result
            ? {
                ...result,
                response: {
                  ...result.response,
                  clockInOut: shiftClockInOut,
                },
              }
            : undefined
        );

        history.replace(submitTimesheetPath, { clockedOut: true });
      }
    },
    [
      modalState,
      chartingReminderModalState,
      skipWait,
      firstStep,
      history,
      submitTimesheetPath,
      shiftId,
      workerId,
      workplaceId,
      step,
      queryClient,
    ]
  );

  function clearShiftDetailsPollingInterval() {
    clearInterval(shiftDetailsPollingInterval.current);
    shiftDetailsPollingInterval.current = undefined;
  }

  // Clear the polling interval when the modal is closed or the component is unmounted
  useEffect(() => {
    if (!modalState.modalIsOpen) {
      clearShiftDetailsPollingInterval();
    }

    return () => {
      clearShiftDetailsPollingInterval();
    };
  }, [modalState.modalIsOpen]);

  const onClockOutSuccess = useCallback(
    async (shiftClockInOut?: TimeRange) => {
      logEvent(APP_V2_USER_EVENTS.TOUCHPOINT_SCAN_SUCCESS, {
        shiftId,
        facilityId: workplaceId,
        workerId,
        action: "clock-out",
      });

      logEvent(APP_V2_USER_EVENTS.CLOCK_OUT_SUCCESS, {
        shiftId,
        workerId,
        workplaceId,
        clockOutType: "touchpoint",
      });

      setStep("clock-out-success");

      await wait(DURATION_OF_TIMEKEEPING_ACTION_SUCCESS_MESSAGE_IN_MS);
      closeBottomSheet(shiftClockInOut);
    },
    [shiftId, workplaceId, workerId, wait, closeBottomSheet]
  );

  const { mutateAsync: refetchShiftDetails } = useGetShiftMutation(shiftId, {
    onSuccess: (shiftDetails) => {
      if (!modalState.modalIsOpen) {
        return;
      }

      if (isDefined(shiftDetails?.response?.clockInOut?.end)) {
        void onClockOutSuccess(shiftDetails.response.clockInOut);
      }
    },
  });

  const handleSkipScan = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.TOUCHPOINT_SCAN_FAILURE, {
      shiftId,
      facilityId: workplaceId,
      workerId,
      action: "clock-out",
    });
    setStep("skip-warning");
  }, [shiftId, workplaceId, workerId]);

  async function recordClockOut() {
    try {
      const postMutationShift = await recordTimekeepingAction({
        shiftId,
        stage: ShiftStage.CLOCK_OUT,
        shiftActionCheck: ActionCheckType.NFC,
      });

      await onClockOutSuccess(postMutationShift.data.clockInOut ?? undefined);
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to clock out, please try again.";

      logEvent(APP_V2_USER_EVENTS.CLOCK_OUT_FAILED, {
        shiftId,
        workerId,
        workplaceId,
        clockOutType: "touchpoint",
        error: errorMessage,
      });

      showErrorToast(errorMessage);

      setStep("touchpoint-nfc-check");
    }
  }

  async function convertToNonInstantPay() {
    try {
      await markNonInstant({
        shiftId,
        reason: ShiftMarkedNonIpReasons.TOUCHPOINT_VALIDATION_FAILURE,
      });

      modalState.closeModal();
    } catch (error: unknown) {
      logEvent(APP_V2_USER_EVENTS.TOUCHPOINT_CONVERSION_TO_NON_INSTANT_FAILED, {
        shiftId,
        facilityId: workplaceId,
        workerId,
        action: "clock-out",
        error: error instanceof Error ? error.message : "Unknown error",
      });

      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to convert to non-instant pay, please try again.";
      showErrorToast(errorMessage);

      closeBottomSheet();
    }
  }

  return (
    <>
      {step === "charting-reminder" && shouldShowChartingReminder && (
        <ClockOutChartingReminder
          modalState={chartingReminderModalState}
          shiftId={shiftId}
          workplaceId={workplaceId}
          onConfirmClockOut={() => {
            setStep("clock-out-confirm");
            chartingReminderModalState.closeModal();
          }}
          onCancel={() => {
            closeBottomSheet();
          }}
        />
      )}

      <BottomSheet modalState={modalState} paperSx={getSafeAreaPaddings({ bottom: true })}>
        {step === "touchpoint-nfc-check" && (
          <WorkerNfcValidation
            isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
            shiftId={shiftId}
            metaProps={{
              workerId,
              workplaceId,
              workplaceName,
            }}
            nfcTagRequests={shift.nfcTagRequests ?? []}
            clockAction={NfcHashValidationAction.CLOCK_OUT}
            onSuccess={() => {
              void recordClockOut();
            }}
            onSkipLocationCheck={async () => {
              setStep("requesting-non-instant-pay");
              await convertToNonInstantPay();
            }}
            onCancel={closeBottomSheet}
            onNfcNotSupported={() => {
              setStep("accept-non-instant-pay");
            }}
          />
        )}

        {step === "clock-out-confirm" && (
          <Stack spacing={3}>
            <BottomSheetIllustrationContent
              illustrationType="clock"
              title="Are you sure you want to clock out?"
            />
            <DialogFooter
              orientation="vertical"
              onClose={() => {
                closeBottomSheet();
              }}
            >
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => {
                  logEvent(APP_V2_USER_EVENTS.CLOCK_OUT_CONFIRM_CONTINUE_TAPPED, {
                    shiftId,
                    workerId,
                    workplaceId,
                  });
                  setStep("instructions");
                }}
              >
                Continue
              </Button>
            </DialogFooter>
          </Stack>
        )}

        {step === "instructions" && (
          <ScanInstructions
            kioskLocation={kioskLocation}
            onContinue={() => {
              setStep("qr-display");
            }}
            onCancel={closeBottomSheet}
          />
        )}

        {step === "qr-display" && (
          <QrCodeDisplay
            qrCodeProps={{
              ...qrCodeProps,
              buttonTapTime: firstViewTime ? new Date(firstViewTime) : undefined,
            }}
            kioskLocation={kioskLocation}
            secondsLeft={secondsLeftForNotWorking}
            onQrCodeReady={() => {
              if (isNil(shiftDetailsPollingInterval.current)) {
                // Start polling for shift changes using the same pattern as legacy TouchPoint
                shiftDetailsPollingInterval.current = setInterval(() => {
                  void refetchShiftDetails(undefined);
                }, REFETCH_SHIFT_INTERVAL_IN_MS);
              }
            }}
            onSkip={handleSkipScan}
            onCancel={() => {
              setStep("instructions");
            }}
          />
        )}

        {step === "clock-out-success" && (
          <TimekeepingActionSuccessContent
            title="Clocked out"
            description="We hope you had a great shift!"
            onClose={closeBottomSheet}
          />
        )}

        {step === "skip-warning" && (
          <TouchpointSkipWarning
            facilityName={workplaceName}
            clockAction="clock-out"
            onCancel={() => {
              setStep("qr-display");
            }}
            onContinue={() => {
              closeBottomSheet();
              onTouchpointFailure();
            }}
          />
        )}

        {step === "accept-non-instant-pay" && (
          <AcceptNonInstantPayContent
            workplaceName={workplaceName}
            isMissedPunchRequestEnabled={isMissedPunchRequestEnabled}
            onAccept={async () => {
              setStep("requesting-non-instant-pay");
              await convertToNonInstantPay();
            }}
            onCancel={() => {
              setStep("clock-out-confirm");
            }}
          />
        )}

        {step === "requesting-non-instant-pay" && (
          <BottomSheetLoadingContent
            title="Converting to non-instant pay"
            description="Should only take a few seconds"
          />
        )}
      </BottomSheet>
    </>
  );
}
/* eslint-enable max-lines */
